import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { BikeIcon, CarIcon, WalkIcon } from "@/assets/icons";
import TabSelect, { TabItem } from "@/components/tabSelect";
import CheckboxList, { CheckboxItem } from "@/components/ui/checkBoxList";
import Section from "@/components/ui/section";
import { MESSAGE } from "@/constants/message";
import { useDebouncedCatchmentSync } from "@/hooks/spatialAnalysis/useDebouncedCatchmentSync";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { setCatchmentSettingsTempRoad } from "@/store/slice/spatialSlice";
import {
  CatchmentTransport,
  ECatchmentTransport,
  ERoadAccessMeasurement,
  ERoadExclusions,
  RoadAccessMeasurement
} from "@/types/spatial";
import { getInvalidContours } from "@/utils/isochrone";

import { CatchmentSettingsFormValues } from "../catchmentSettings";
import InputEntry from "./inputEntry";

const TRANSPORT_OPTIONS: TabItem[] = [
  {
    label: "Walking",
    value: ECatchmentTransport.WALKING,
    icon: <WalkIcon />
  },
  {
    label: "Cycling",
    value: ECatchmentTransport.CYCLING,
    icon: <BikeIcon />
  },
  {
    label: "Driving",
    value: ECatchmentTransport.DRIVING,
    icon: <CarIcon />
  }
];

const MEASUREMENT_OPTIONS: TabItem[] = [
  {
    label: "Distance",
    value: ERoadAccessMeasurement.DISTANCE
  },
  {
    label: "Duration",
    value: ERoadAccessMeasurement.DURATION
  }
];

const CatchmentByRoadAccess = () => {
  const dispatch = useDispatch();
  const form = useFormContext<CatchmentSettingsFormValues>();
  const { isNewRoadExclusionEnabled } = useAppFeatures();
  const { catchmentSettingsTemp } = useSelector(
    (state: RootState) => state.spatial
  );

  const checkboxItems: CheckboxItem[] = useMemo(
    () => [
      { id: ERoadExclusions.TOLL, label: "Toll", value: ERoadExclusions.TOLL },
      isNewRoadExclusionEnabled
        ? {
            id: ERoadExclusions.MOTORWAY,
            value: ERoadExclusions.MOTORWAY,
            label: "Motorway"
          }
        : {
            id: ERoadExclusions.CYCLING_PATH,
            value: ERoadExclusions.CYCLING_PATH,
            label: "Cycling Path"
          },
      {
        id: ERoadExclusions.FERRY,
        value: ERoadExclusions.FERRY,
        label: "Ferry"
      },
      {
        id: ERoadExclusions.UNPAVED,
        value: ERoadExclusions.UNPAVED,
        label: "Unpaved"
      }
    ],
    [isNewRoadExclusionEnabled]
  );

  const invalidContours = getInvalidContours(
    catchmentSettingsTemp.roadAccessConfig?.isochroneData ?? []
  );

  const durations = useWatch({ name: "durations", control: form.control });
  const distances = useWatch({ name: "distances", control: form.control });

  const handleCheckboxListChange = (selected: string[]) => {
    form.setValue("roadExclusions", selected as ERoadExclusions[]);
    dispatch(
      setCatchmentSettingsTempRoad({
        roadExclusions: selected as ERoadExclusions[]
      })
    );
  };

  const debouncedSyncRoadAccess = useDebouncedCatchmentSync(
    setCatchmentSettingsTempRoad
  );

  const handleSelectCatchment = (value: string) => {
    // Keep exclusions stored so they're restored on return to Driving; they're
    // only applied for Driving (gated via getEffectiveRoadExclusions).
    dispatch(
      setCatchmentSettingsTempRoad({
        routingType: value as CatchmentTransport
      })
    );
  };

  const handleSelectMeasurement = (value: string) => {
    dispatch(
      setCatchmentSettingsTempRoad({
        measurementType: value as RoadAccessMeasurement
      })
    );
  };
  const filteredInvalidContours = invalidContours.filter(
    (x): x is number => typeof x === "number"
  );

  // Disabling exclusions for non-Driving routing is new behavior; keep it behind
  // the flag so exclusions stay always-enabled when the flag is off.
  const isRoadExclusionDisabled =
    isNewRoadExclusionEnabled &&
    catchmentSettingsTemp.roadAccessConfig?.routingType !==
      ECatchmentTransport.DRIVING;

  return (
    <div className="flex flex-col mt-2 gap-2">
      {/* Routing */}
      <Section label="Routing">
        <TabSelect
          className="bg-transparent h-auto gap-2"
          buttonClassName="flex flex-col text-xs w-[94px] h-[72px] btn-default font-bold"
          buttonSelectedClassName="!bg-teal-50 !border-teal-500"
          wrapperListClassName="w-full justify-between gap-1"
          tabList={TRANSPORT_OPTIONS}
          selected={catchmentSettingsTemp.roadAccessConfig?.routingType}
          onSelect={handleSelectCatchment}
        />
      </Section>
      {/* Road Exclusions */}
      <Section label="Road Exclusions">
        <CheckboxList
          items={checkboxItems}
          selectedValue={
            isRoadExclusionDisabled
              ? []
              : catchmentSettingsTemp.roadAccessConfig?.roadExclusions
          }
          onChange={handleCheckboxListChange}
          disabled={isRoadExclusionDisabled}
          className="grid grid-cols-2"
        />
      </Section>
      {/* Measurement */}
      <Section label="Measurement">
        <TabSelect
          className="bg-transparent h-auto gap-2"
          buttonClassName="flex flex-col text-xs h-[24px] btn-default font-bold w-fit px-2"
          buttonSelectedClassName="!bg-teal-50 !border-teal-500"
          wrapperListClassName="w-full gap-1"
          tabList={MEASUREMENT_OPTIONS}
          selected={catchmentSettingsTemp.roadAccessConfig?.measurementType}
          onSelect={handleSelectMeasurement}
        />
      </Section>
      {/* Measurement Input By type */}

      {catchmentSettingsTemp.roadAccessConfig?.measurementType ===
        ERoadAccessMeasurement.DISTANCE && (
        <>
          <span className="text-[10px]">
            {MESSAGE.CATCHMENT_CONTOUR_INVALID_DISTANCE}
          </span>
          <InputEntry
            maxItem={3}
            showTrashIcon={distances?.length > 1}
            fieldName="distances"
            maxValue={100000}
            placeHolder={MESSAGE.COVERAGE_TYPE.PLACEHOLDER_TYPE_DISTANCE}
            onChange={() => debouncedSyncRoadAccess("distances")}
            invalidValues={filteredInvalidContours}
            errorMessage={MESSAGE.CATCHMENT_CONTOUR_INVALID}
            testId="catchment-by-distance"
          />
        </>
      )}
      {catchmentSettingsTemp.roadAccessConfig?.measurementType ===
        ERoadAccessMeasurement.DURATION && (
        <>
          <span className="text-[10px]">
            {MESSAGE.CATCHMENT_CONTOUR_INVALID_DURATION}
          </span>
          <InputEntry
            maxItem={3}
            fieldName="durations"
            maxValue={60}
            showTrashIcon={durations?.length > 1}
            placeHolder={MESSAGE.COVERAGE_TYPE.PLACEHOLDER_TYPE_DURATION}
            onChange={() => debouncedSyncRoadAccess("durations")}
            invalidValues={filteredInvalidContours}
            errorMessage={MESSAGE.CATCHMENT_CONTOUR_INVALID}
            testId="catchment-by-duration"
          />
        </>
      )}
    </div>
  );
};

export default CatchmentByRoadAccess;
